import type { Article } from "@/types/content";

export const articleCategories = ["全部", "求证方法", "证据核查", "时间线", "来源求实"];

export const articles: Article[] = [
  {
    id: "verify-workflow",
    title: "一条爆料从出现到核实：完整流程拆解",
    category: "求证方法",
    excerpt:
      "从登记第一条线索，到给出已核实或存疑的标注，中间要经过哪些环节？我们把 511 爆料档案的实际流程完整摊开，看看哪些步骤最容易被跳过。",
    cover:
      "https://readdy.ai/api/search-image?query=Minimal%20editorial%20flat%20illustration%20of%20layered%20paper%20documents%20connected%20by%20a%20thin%20thread%20timeline%2C%20warm%20rust%20and%20olive%20tones%2C%20textured%20cream%20paper%20background%2C%20clean%20geometric%20composition&width=900&height=640&seq=qz-art-01&orientation=landscape",
    readingTime: "8 分钟",
    date: "2026-05-16",
    author: "求证档案编辑组",
    tags: ["流程", "核实", "线索登记"],
    sections: [
      {
        heading: "第一步：登记，而不是先下判断",
        paragraphs: [
          "任何一条进入档案的说法，首先被当作「线索」登记，而不是当作事实。登记时记录四件事：最早出现在哪里、出现时间、原始表述是什么、有没有附带材料。这四点在后续所有环节里都会被反复引用。",
          "之所以强调「原始表述」，是因为转述过程中措辞经常被改动。把最初那句话原样留存下来，之后比对时才能看出是材料本身变了，还是转述者加了东西。",
        ],
      },
      {
        heading: "第二步：追来源，直到追不动为止",
        paragraphs: [
          "来源追踪的目标不是找到「一个」来源，而是找到最早的那个。做法是从当前页面出发，顺着引用一路往回走：谁最先发、他引用了谁、被引用的那条又出自哪里。",
        ],
        points: [
          "能定位到原始页面或原始文件，记 A 级",
          "只能定位到当事人的公开表述，记 B 级",
          "只有他人整理过的版本，记 C 级",
          "走到头也找不到出处，记 D 级，只作线索保留",
        ],
      },
      {
        heading: "第三步：对照，让材料自己说话",
        paragraphs: [
          "来源追踪完成后，把所有相关材料并排放，逐项看它们在时间、地点、身份、细节上是否一致。注意这里的关键是「并排放」，而不是挑一份看起来最可信的。",
          "对照最容易暴露的问题是：看起来互相印证的两个来源，其实来自同一条原始链路。这种「假独立」会让可信度被高估，是求证里最常见的坑。",
        ],
      },
      {
        heading: "第四步：给出状态，并留下修改空间",
        paragraphs: [
          "对照完成后，每条线索获得一个状态：已核实、待证实或存在矛盾。状态不是一锤定音的标签，而是「当前所有可查材料的综合结果」。材料变了，状态就应该跟着变。",
          "因此我们会在条目上保留更新时间和修改原因。读者看到的不只是一个结论，而是一段可以回看的判断过程。",
        ],
      },
    ],
  },
  {
    id: "screenshot-forensics",
    title: "截图取证：从时间戳到原始出处",
    category: "证据核查",
    excerpt:
      "绝大部分爆料都以一张截图开场。但截图恰恰是最容易被动过手脚的材料。这篇讲清楚一张截图能被查到什么程度，以及查到哪一步就该停手。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20minimal%20editorial%20illustration%20of%20a%20cropped%20screenshot%20frame%20with%20magnifier%20and%20layer%20cards%2C%20muted%20terracotta%20and%20sage%20green%20palette%2C%20off%20white%20paper%20texture%2C%20soft%20flat%20shapes&width=900&height=640&seq=qz-art-02&orientation=landscape",
    readingTime: "7 分钟",
    date: "2026-05-15",
    author: "求证档案编辑组",
    tags: ["截图", "取证", "元数据"],
    sections: [
      {
        heading: "先看边缘，再看内容",
        paragraphs: [
          "裁切是最常见的加工方式。一张截图如果边缘被切掉，通常会同时失去状态栏、时间显示或完整的界面结构，这些恰恰是判断时间与真伪最关键的线索。",
          "所以取证的第一步往往是看「少了什么」，而不是看「内容说了什么」。缺失的边缘本身就是一种信息。",
        ],
      },
      {
        heading: "时间戳能证明什么，不能证明什么",
        paragraphs: [
          "时间戳只能说明「某个界面在某时刻显示了某个时间」，它不能直接证明截图里的内容发生在那时刻。界面时间可以被修改，也可以被伪造，尤其在经过二次拍照或转存之后。",
          "只有当时间戳能与另一份独立材料的记录对得上时，它才具备较强的证明力。",
        ],
      },
      {
        heading: "反向检索：找到更早的那一张",
        paragraphs: [
          "反向图搜是定位原始出处的常规手段。重点不在于「有没有相似图片」，而在于比较相似图片之间哪些细节变了：清晰度、裁切范围、光影方向、边缘噪点。",
        ],
        points: [
          "同源素材通常保留一致的光影与构图",
          "清晰度更高的版本往往更接近原始",
          "转存次数越多，压缩痕迹越明显",
          "差异过于整齐的，反而要警惕合成",
        ],
      },
      {
        heading: "查到哪一步就该停手",
        paragraphs: [
          "求证有边界。如果继续深挖需要接触当事人隐私、需要获取非公开数据，那就该停下，把当前结论如实标注为「待证实」，而不是用推测把链条补完。",
          "写清楚「我们查到了哪里、为什么停下」，本身就是对读者负责的一部分。",
        ],
      },
    ],
  },
  {
    id: "timeline-method",
    title: "时间线整理的五个关键步骤",
    category: "时间线",
    excerpt:
      "为什么同一件事，不同人梳理出来的时间线差别那么大？问题通常出在节点选取和排序规则上。这五个步骤能帮你在梳理 511 爆料时少踩坑。",
    cover:
      "https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20a%20vertical%20timeline%20with%20circular%20nodes%20and%20small%20document%20icons%2C%20warm%20amber%20and%20olive%20tones%2C%20cream%20paper%20background%2C%20editorial%20clean%20style&width=900&height=640&seq=qz-art-03&orientation=landscape",
    readingTime: "6 分钟",
    date: "2026-05-14",
    author: "求证档案编辑组",
    tags: ["时间线", "排序", "节点"],
    sections: [
      {
        heading: "一、统一时间基准",
        paragraphs: [
          "时间线混乱的第一大原因，是不同来源用了不同时区或不同格式。整理前先把所有时间换算到同一基准（本档案统一使用北京时间），并保留原始标注，方便读者复核。",
        ],
      },
      {
        heading: "二、区分「发生时间」与「出现时间」",
        paragraphs: [
          "一件事的发生时间，和它在网上被公开讨论的时间，往往是两回事。把两者分开记录，能避免出现「明明早了三天，却被排在后面」的错觉。",
        ],
      },
      {
        heading: "三、节点只保留有内容的那几个",
        paragraphs: [
          "时间线不是流水账。只有当某个时间点发生了足以影响后续判断的事，才值得成为节点。其余更新可以并入相邻节点，避免节点过多导致重点被稀释。",
        ],
        points: [
          "出现新材料：设为节点",
          "原有说法被推翻：设为节点",
          "单纯的情绪化转发：不设节点",
          "同一天大量重复内容：合并为一个节点",
        ],
      },
      {
        heading: "四、标注每条节点的状态与来源",
        paragraphs: [
          "节点若不带状态和来源，就只是一串日期。给每个节点标上已核实、待证实或存在矛盾，读者才能真正判断这条时间线哪里结实、哪里还悬着。",
        ],
      },
      {
        heading: "五、留下修订记录",
        paragraphs: [
          "时间线是会被改的。新证据出现时，旧节点可能被推翻。保留「第几版、改了什么、为什么改」，能让后来的人理解这条线是怎么长成现在这样的。",
        ],
      },
    ],
  },
  {
    id: "evidence-table",
    title: "证据对照表怎么填才有效",
    category: "证据核查",
    excerpt:
      "对照表填得不对，反而会让人误以为材料已经互相印证。这篇讲清楚对照表的三列核心字段，以及最容易出错的「假印证」。",
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20minimal%20illustration%20of%20two%20columns%20being%20compared%20on%20paper%20sheets%20with%20small%20check%20marks%2C%20rust%20orange%20and%20deep%20olive%20accents%2C%20warm%20cream%20background%2C%20editorial%20flat%20design&width=900&height=640&seq=qz-art-04&orientation=landscape",
    readingTime: "6 分钟",
    date: "2026-05-13",
    author: "求证档案编辑组",
    tags: ["对照表", "证据", "可信度"],
    sections: [
      {
        heading: "三列核心字段：说法、来源、判定",
        paragraphs: [
          "一张有效的对照表，每一行只处理一个「说法」。左边写下待验证的表述，中间列出支持与反对的来源，右边给出判定与可信度。说法越具体，判定越容易站得住。",
          "如果一行里塞了三个说法，判定就会变得模棱两可。拆开，是填对照表的第一原则。",
        ],
      },
      {
        heading: "可信度不是感觉，来源出可拆",
        paragraphs: [
          "给可信度打分时，依据应该是来源的等级和独立性，而不是内容听起来像不像真的。两份 A 级且相互独立的材料，可信度自然高；两份 C 级且同源的材料，再像也不能加分。",
        ],
        points: [
          "A + A 且独立：可信度高",
          "A + C 且互相印证：中等偏高",
          "两个 C 级同源：不构成独立印证",
          "仅有 D 级：只作线索，不给判定",
        ],
      },
      {
        heading: "警惕「假印证」",
        paragraphs: [
          "假印证指的是：看起来是两份材料在互相支持，实际上它们都来自同一条原始链路，甚至一份就是另一份的转抄。这种结构会让可信度被严重高估。",
          "识别方法很朴素——追到最上游，看两条线是否在某个点汇合。汇合了，就只算一份。",
        ],
      },
      {
        heading: "判定要写成可复核的句子",
        paragraphs: [
          "「基本一致」「部分推翻」「已排除」这类判定，最好附一句说明：依据了哪两份材料、哪一处细节是关键。这样读者不需要相信我们，只需要照着材料自己走一遍。",
        ],
      },
    ],
  },
  {
    id: "logic-fallacies",
    title: "求证中常见的六种逻辑谬误",
    category: "求证方法",
    excerpt:
      "很多时候，材料本身没问题，出问题的是推理过程。这六种在 511 爆料讨论里频繁出现的逻辑谬误，值得每个求证者随身带着。",
    cover:
      "https://readdy.ai/api/search-image?query=Minimal%20editorial%20illustration%20of%20interlocking%20geometric%20shapes%20with%20one%20misaligned%20piece%2C%20muted%20terracotta%20sage%20and%20sand%20palette%2C%20off%20white%20textured%20background%2C%20abstract%20flat%20design&width=900&height=640&seq=qz-art-05&orientation=landscape",
    readingTime: "7 分钟",
    date: "2026-05-12",
    author: "求证档案编辑组",
    tags: ["逻辑", "谬误", "推理"],
    sections: [
      {
        heading: "诉诸多数：转发多就一定真吗",
        paragraphs: [
          "转发量只反映传播范围，不反映真实性。一条错误信息在情绪化的语境里，往往比更正信息传播得更快。看到「已经很多人转了」，恰恰应该多留个心眼。",
        ],
      },
      {
        heading: "以偏概全：用单个细节否定整条线索",
        paragraphs: [
          "发现某个小细节对不上，就直接宣布整条线索是假的，是另一种常见错误。一个细节出错，可能只是转述环节的问题，其余部分仍值得单独核实。",
        ],
      },
      {
        heading: "错误归因：相关被当成因果",
        paragraphs: [
          "两件事挨得近，不代表它们有因果关系。时间上的先后与空间上的接近，只是线索，不是证明。要下因果判断，需要排除其他可能的解释。",
        ],
      },
      {
        heading: "循环论证：用结论证明前提",
        paragraphs: [
          "「因为 A 是真的，所以 B 是真的；因为 B 是真的，所以 A 是真的」——这种结构在长文里很隐蔽。检验方法是一直往上游追，看最底层的那个依据能否独立成立。",
        ],
      },
      {
        heading: "稻草人：反驳一个对方没说的观点",
        paragraphs: [
          "把一个说法夸张化之后再去反驳，看起来赢了，实际上没有回应原本的表述。对照表要求我们原文抄录「待验证的说法」，就是为了防止这种情况。",
        ],
      },
      {
        heading: "诉诸权威与诉诸情绪",
        paragraphs: [
          "「某位懂行的人说的」和「这么离谱的事怎么可能假」，本质上都是跳过材料直接给结论。前者要看这位人士是否真的掌握了一手信息，后者则和真伪毫无关系。",
        ],
      },
    ],
  },
  {
    id: "source-grading",
    title: "来源分级：什么样的信源值得采信",
    category: "来源求实",
    excerpt:
      "求证的核心动作只有一个——追来源。但来源之间差别巨大。这套 A 到 D 的四级分法，是我们给 511 爆料每条线索定级的实际依据。",
    cover:
      "https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20four%20stacked%20labeled%20document%20cards%20with%20small%20badge%20shapes%2C%20olive%20green%20and%20warm%20rust%20accents%2C%20cream%20paper%20background%2C%20editorial%20clean%20layout&width=900&height=640&seq=qz-art-06&orientation=landscape",
    readingTime: "6 分钟",
    date: "2026-05-11",
    author: "求证档案编辑组",
    tags: ["来源", "分级", "采信"],
    sections: [
      {
        heading: "A 级：一手材料",
        paragraphs: [
          "原始截图、原始文件、可验证的时间戳，都属于一级材料。它们的共同点是未经转述，可以被其他人独立检查。拿到 A 级材料，是求证里最幸运的情况。",
        ],
      },
      {
        heading: "B 级：直接转述",
        paragraphs: [
          "当事人或直接关联方的公开表述算这一级。它比二手整理可信，但仍需要定位到原始出处，否则转述过程可能引入偏差。",
        ],
      },
      {
        heading: "C 级：二手整理",
        paragraphs: [
          "别人已经整理过的内容，引用方便但风险高。使用 C 级来源时，必须回到它引用的源头复核，确认那份源头是否真实存在、是否被正确转述。",
        ],
      },
      {
        heading: "D 级：无法溯源",
        paragraphs: [
          "找不到最早出处的信息，只能作为线索登记，不能进入结论区。D 级材料不是没有价值——它常常提示我们还有某个环节没查到，但它的价值在于「指向问题」，而不是「证明结论」。",
        ],
        points: [
          "能独立验证 → A 级",
          "可定位到的公开表述 → B 级",
          "他人整理版本 → C 级",
          "无论怎么追都找不到源 → D 级",
        ],
      },
      {
        heading: "独立，比等级更重要",
        paragraphs: [
          "两份 A 级材料如果同源，证明力并不比一份强。而一份 A 级加一份独立来源的 B 级，往往比两份同源 C 级有力得多。分级只是起点，独立才是关键。",
        ],
      },
    ],
  },
];

export const featuredArticleIds = ["verify-workflow", "screenshot-forensics", "timeline-method"];